<template>
    <div class="border-slate-200 dark:border-slate-700" :class="{ 'border-t': index !== 0 }">
        <!-- Card Header Row -->
        <div
            class="flex items-center hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors py-4 cursor-pointer"
            @click="toggleExpanded"
        >
            <!-- Benefit Column -->
            <div
                class="flex items-center px-6 min-w-0"
                :class="getBenefitColumnWidth()"
            >
                <div
                    class="flex items-center gap-3 min-w-0 flex-1"
                    v-if="viewMode === 'by_card'"
                >
                    <!-- Card Image -->
                    <div class="shrink-0">
                        <ImageLoader>
                            <template #image>
                                <img
                                    :src="`${cdnUrl}/${cardGroup?.card?.image?.path}/_small/${cardGroup?.card?.image?.file_name}`"
                                    :alt="cardGroup?.card?.title"
                                    class="w-12 h-8 rounded-sm"
                                />
                            </template>
                            <template #error>
                                <div
                                    class="w-12 h-8 bg-slate-100 dark:bg-slate-700 rounded-sm border border-slate-200 dark:border-slate-600 flex items-center justify-center"
                                >
                                    <svg
                                        class="w-4 h-4 text-slate-400 dark:text-slate-500"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                            </template>
                        </ImageLoader>
                    </div>

                    <!-- Card Details -->
                    <div class="min-w-0 flex-1">
                        <div
                            class="font-medium text-slate-800 dark:text-slate-100 text-sm flex items-center gap-1"
                        >
                            <div class="relative min-w-0 group">
                                <span class="block truncate cursor-pointer">
                                    {{
                                        cardGroup?.card?.title || 'Unknown Card'
                                    }}
                                </span>
                                <div
                                    class="absolute top-full left-0 py-1 px-2 text-sm text-white bg-slate-800 dark:bg-slate-700 rounded-lg shadow-lg dark:shadow-slate-900/50 z-50 whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none"
                                    role="tooltip"
                                >
                                    {{
                                        cardGroup?.card?.title || 'Unknown Card'
                                    }}
                                </div>
                            </div>
                            <span
                                v-if="
                                    showLastFour &&
                                    cardGroup.connected_account_last_four
                                "
                                class="text-slate-500 dark:text-slate-400 text-xs shrink-0"
                            >
                                ({{ cardGroup.connected_account_last_four }})
                            </span>
                        </div>
                        <div
                            class="text-slate-500 dark:text-slate-400 text-sm py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg max-w-fit font-medium"
                            v-show="formattedTotalValue"
                        >
                            Up to {{ formattedTotalValue }}/year
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-3 min-w-0 flex-1" v-else>
                    <!-- Icon -->
                    <div class="shrink-0"></div>

                    <!-- Card Details -->
                    <div class="min-w-0 flex-1">
                        {{ cardGroup.benefit_category }}
                    </div>
                </div>
            </div>

            <!-- User Column - Always present for alignment, content hidden in by_card mode -->
            <div
                v-if="getUserColumn()"
                class="px-6 flex items-center"
                :class="getUserColumn().width"
            >
                <div class="flex items-center gap-2">
                    <Icon icon="user" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <div class="flex flex-col items-start">
                        <div
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                        >
                            {{ getFirstBenefitUser() }}
                        </div>
                    </div>
                </div>
                <!-- Empty space when viewMode is 'by_card' to maintain column alignment -->
            </div>

            <!-- Used Current Period Column -->
            <div
                class="px-6 py-1 gap-2 flex flex-col"
                :class="getUsedCurrentPeriodColumn().width"
            >
                <div v-if="viewMode === 'by_card'">
                    <!-- Locked state: show "Upgrade to track" -->
                    <button
                        v-if="cardGroup.isLocked"
                        class="flex items-center gap-1 text-orange text-sm font-medium hover:underline cursor-pointer bg-transparent border-none p-0"
                        @click.stop="openUpgradeModal"
                    >
                        <Icon icon="lock" class="w-4 h-4" />
                        <span>Upgrade to track</span>
                    </button>
                    <!-- Normal state: show progress bar -->
                    <template v-else>
                        <div
                            class="w-full max-w-[120px] bg-orange-200 rounded-full h-1 relative"
                        >
                            <div
                                class="bg-10x-orange h-1 rounded-full transition-all"
                                :style="{ width: `${calculatedUsagePercentage}%` }"
                            ></div>
                            <!-- 100% Completion Indicator -->
                            <div
                                v-if="calculatedUsagePercentage >= 100"
                                class="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                            >
                                <Icon
                                    icon="check"
                                    svgClass="w-2 h-2 text-white"
                                    class="text-white"
                                />
                            </div>
                        </div>
                        <div
                            class="flex items-center text-sm text-slate-500 dark:text-slate-400 font-normal mt-2"
                        >
                            <span>
                                {{ formattedUsedAmount }}
                            </span>
                            <span class="ml-1">of {{ formattedTotalValue }}</span>
                            <span class="ml-1 text-xs">(used YTD)</span>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Expires In Column - Always present for alignment -->
            <div
                v-if="getExpiresInColumn()"
                class="px-6"
                :class="getExpiresInColumn().width"
            >
                <!-- Content only shown in flat mode -->
                <div v-if="viewMode === 'flat'">
                    <!-- Expires in content would go here -->
                </div>
            </div>

            <!-- Actions Column -->
            <div
                class="px-6 flex items-center justify-end gap-2 w-32 shrink-0"
            >
                <button
                    class="btn-icon !rounded-full"
                    @click.stop="toggleExpanded"
                >
                    <Icon
                        icon="chevron-down"
                        svgClass="m-1"
                        class="w-3 h-3 transition-all duration-200"
                        :class="{ 'rotate-180': isExpanded }"
                    />
                </button>
            </div>
        </div>

        <!-- Expanded Benefits -->
        <div v-if="isExpanded" class="">
            <BenefitsTableRow
                v-for="(benefit, index) in cardGroup.benefits"
                :key="`${benefit.benefit.id}-${benefit.connected_account_id}`"
                :benefit-item="benefit"
                :columns="columns"
                :isAGroup="true"
                :index="index"
                class="ml-12"
                @add-usage="$emit('add-usage', $event)"
                @view-details="$emit('view-details', $event)"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import BenefitsTableRow from './benefits-table-row.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Tooltip from '@/components/tooltip.vue';
import filters from '@/filters';

const props = defineProps({
    cardGroup: {
        type: Object,
        required: true,
    },
    columns: {
        type: Array,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['add-usage', 'view-details']);

const store = useCardBenefitsStore();
const { showLastFour, viewMode, filteredColumns } = storeToRefs(store);

const isExpanded = ref(false);

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

// Check if all benefits in the group are count-based
const allBenefitsAreCountBased = computed(() => {
    return props.cardGroup.benefits.every(
        (benefit) => benefit.benefit?.values_summary?.is_count_based ?? false
    );
});

// Check if all benefits are purely count-based (count but no dollar value)
const allPurelyCountBased = computed(() => {
    return props.cardGroup.benefits.every((benefit) => {
        const summary = benefit.benefit?.values_summary;
        return (
            summary?.currency === 'NCRED' ||
            (summary?.is_count_based && !parseFloat(benefit.benefit?.amount || 0))
        );
    });
});

// Calculate YTD usage across all benefits in the group.
// For monetary benefits, uses backend total_used_ytd (calendar-year sum)
// with optimistic deltas for instant UI updates.
// For purely count-based benefits (amount=0), total_used_ytd is always 0
// because the backend sums total_amount_used which is 0 for counts, so
// we sum current_period_usage directly from the live store instead.
const calculatedYtdUsage = computed(() => {
    // Purely count-based: sum current_period_usage from live store
    // (backend total_used_ytd is 0 for these since total_amount_used=0)
    if (allPurelyCountBased.value) {
        return props.cardGroup.benefits.reduce((sum, benefit) => {
            const liveBenefit =
                store.benefits.find(
                    (sb) =>
                        sb.benefit.id === benefit.benefit.id &&
                        sb.connected_account_id ===
                            benefit.connected_account_id
                ) || benefit;
            return sum + parseFloat(liveBenefit.current_period_usage || 0);
        }, 0);
    }

    // Monetary: use backend total_used_ytd + optimistic deltas
    const backendYtd = parseFloat(props.cardGroup.total_used_ytd || 0);

    let optimisticDelta = 0;
    for (const benefit of props.cardGroup.benefits) {
        const liveBenefit = store.benefits.find(
            (sb) =>
                sb.benefit.id === benefit.benefit.id &&
                sb.connected_account_id === benefit.connected_account_id
        );
        if (!liveBenefit) continue;

        // Skip count-based and NCRED benefits in mixed groups
        if (store.checkIsCountBased(liveBenefit.benefit)) {
            continue;
        }

        const liveVal = parseFloat(liveBenefit.current_period_usage || 0);
        const origVal = parseFloat(benefit.current_period_usage || 0);
        optimisticDelta += liveVal - origVal;
    }

    return backendYtd + optimisticDelta;
});

const calculatedUsagePercentage = computed(() => {
    const totalUsedYtd = calculatedYtdUsage.value;

    if (allBenefitsAreCountBased.value) {
        // For count-based benefits, calculate total available count
        const totalAvailableCount = props.cardGroup.benefits.reduce(
            (sum, benefit) => {
                const maxCount = benefit.benefit?.value_details?.count || 0;
                return sum + maxCount;
            },
            0
        );

        if (totalAvailableCount === 0) return 0;

        const percentage = (totalUsedYtd / totalAvailableCount) * 100;
        if (isNaN(percentage)) return 0;

        return Math.min(Math.round(percentage), 100);
    } else {
        // For monetary benefits, use YTD usage against annual value
        const totalAvailable = calculatedTotalAnnualValue.value;

        if (totalAvailable === 0) return 0;

        const percentage = (totalUsedYtd / totalAvailable) * 100;
        if (isNaN(percentage)) return 0;

        return Math.min(Math.round(percentage), 100);
    }
});

const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
};

const calculatedTotalAnnualValue = computed(() => {
    let total = 0;
    const { start: ytdStart, end: ytdEnd } = store.periodBoundaries.annual;

    // Get live benefits from store to ensure reactivity to optimistic updates
    const liveBenefits = props.cardGroup.benefits.map((b) => {
        return (
            store.benefits.find(
                (sb) =>
                    sb.benefit.id === b.benefit.id &&
                    sb.connected_account_id === b.connected_account_id
            ) || b
        );
    });

    for (const benefitItem of liveBenefits) {
        const benefit = benefitItem.benefit;
        if (!benefit) continue;

        // Skip count-based benefits without a dollar amount, and NCRED benefits
        if (
            benefit.values_summary?.currency === 'NCRED' ||
            (benefit.values_summary?.is_count_based && !parseFloat(benefit.amount))
        ) {
            continue;
        }

        // Skip multi-year benefits that were used in a previous year
        if (!store.isBenefitActiveThisYear(benefitItem, ytdStart, ytdEnd)) {
            continue;
        }

        // Get the display amount
        const displayAmount = store.calculateDisplayAmount(benefitItem);

        // Special handling for monthly benefits with override
        const count = parseFloat(benefit.value_details?.count) || 1;
        let annualValue;

        if (
            benefit.period_type?.toUpperCase() === 'MONTHLY' &&
            benefit.value_details?.override_month
        ) {
            // Calculate: (base * 11 months) + (override * 1 month)
            // The override REPLACES the base amount for that specific month
            const baseAmount = parseFloat(benefit.amount) || 0;
            const overrideValue =
                parseFloat(benefit.value_details.override_month.value) || 0;

            annualValue = baseAmount * count * 11 + overrideValue * count;
        } else {
            // Convert to annual value based on period type
            annualValue = store.convertPeriodValueToAnnual(
                displayAmount,
                benefit.period_type,
                count
            );
        }

        total += annualValue;
    }

    return total;
});

const formattedTotalValue = computed(() => {
    if (allPurelyCountBased.value) {
        // For purely count-based benefits, sum the available counts
        const totalCount = props.cardGroup.benefits.reduce((sum, benefit) => {
            const count =
                parseFloat(benefit.benefit?.value_details?.count) || 0;
            return sum + count;
        }, 0);
        return totalCount.toString();
    } else {
        // For monetary benefits, format as money
        const total = calculatedTotalAnnualValue.value;
        return total > 0 ? filters.formatSmartMoney(total) : null;
    }
});

const formattedUsedAmount = computed(() => {
    const totalUsedYtd = calculatedYtdUsage.value;

    if (allPurelyCountBased.value) {
        // For purely count-based benefits, return as string
        return totalUsedYtd.toString();
    } else {
        // For monetary benefits, format as money
        return filters.formatSmartMoney(totalUsedYtd);
    }
});

// Column width helper methods
const getBenefitColumnWidth = () => {
    const benefitColumn = filteredColumns.value.find(
        (col) => col.key === 'benefit'
    );
    return benefitColumn?.width || 'flex-1 min-w-[300px]';
};

const getUserColumn = () => {
    return filteredColumns.value.find((col) => col.key === 'user');
};

const getUsedCurrentPeriodColumn = () => {
    const column = filteredColumns.value.find(
        (col) => col.key === 'used_current_period'
    );
    return column || { width: 'w-48 shrink-0' };
};

const getExpiresInColumn = () => {
    const column = filteredColumns.value.find(
        (col) => col.key === 'expires_in'
    );
    return column || { width: 'w-32 shrink-0' };
};

const getFirstBenefitUser = () => {
    if (props.cardGroup.benefits && props.cardGroup.benefits.length > 0) {
        return props.cardGroup.benefits[0].user?.name || 'Unknown User';
    }
    return 'Unknown User';
};

// Open upgrade modal for locked card groups
const openUpgradeModal = () => {
    store.setShowUpgradeModal(true);
};
</script>
